import { useState, useMemo, useRef, useEffect } from 'react';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { useParams, useSearchParams } from 'react-router-dom';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { fmt2 } from '@/shared/lib/formatters';
import { SlideModal } from '@/shared/ui/SlideModal';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Tooltip as ChartTooltip, Filler, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ZoomPlugin from 'chartjs-plugin-zoom';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { useAlertsLive, useAlertsHistorical, useAlertsFilterSchema } from '@/features/alerts/hooks/useAlerts';
import { useParameterCharts } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { lastNDaysRangeFrom } from '@/shared/lib/dateRange';
import { RangePicker } from '@/shared/ui/RangePicker';
import { ALERT_CATEGORY_META, ALERT_CATEGORY_ORDER, SEVERITY_META, catBg, sevBg } from '@/features/alerts/alertMeta';
import { HistoricalFilterBar, histDateParams } from '@/shared/ui/HistoricalFilterBar';
import { FilterDrawer } from '@/shared/ui/FilterDrawer';
import { buildFilterParams } from '@/shared/lib/filterParams';
import { ViewToggle } from '@/shared/ui/ViewToggle';
import { AppTable } from '@/shared/ui/AppTable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Filler, Legend, ZoomPlugin);

// Padding around each kanban scroll list, sized to the scrollbar width so the bar
// lives in the gutter instead of over the chips (AUT-165).
const KANBAN_SCROLL_PAD = 8;

const ZOOM_CFG = {
  pan: { enabled: false },
  zoom: {
    wheel: { enabled: false }, pinch: { enabled: false }, mode: 'xy',
    drag: { enabled: true, backgroundColor: 'rgba(30,67,150,0.07)', borderColor: 'rgba(30,67,150,0.25)', borderWidth: 1 },
  },
};



// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtLabel(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function relTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - Date.parse(iso);
  if (Number.isNaN(diff)) return '';
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// Absolute timestamp, e.g. "Jun 17, 1:00 PM" (AUT-185).
function fmtTs(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Human-readable duration from a minute count: <60 → "Xm", <24h → "Xh Ym", else "Xd Yh".
function fmtDuration(min) {
  if (min == null || Number.isNaN(min)) return '?';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm ? `${h}h ${rm}m` : `${h}h`;
  }
  const d = Math.floor(m / 1440);
  const rh = Math.round((m % 1440) / 60);
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

// Relative time from now to a future ISO timestamp: "in 2h 15m", "in 45m", etc.
function fmtRelativeFuture(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMin = Math.round((d.getTime() - Date.now()) / 60000);
  if (diffMin <= 0) return 'imminently';
  if (diffMin < 60) return `in ${diffMin}m`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m ? `in ${h}h ${m}m` : `in ${h}h`;
}

function severityColor(sev) {
  return SEVERITY_META[sev]?.color ?? '#64748b';
}

// ── Alert Trend Chart (inline RunChart with forecast dashed line) ─────────────
const TREND_RANGES = [
  { key: '1d',  label: '1D',  days: 1  },
  { key: '7d',  label: '7D',  days: 7  },
  { key: '14d', label: '14D', days: 14 },
  { key: '30d', label: '30D', days: 30 },
];

function AlertTrendChart({ alert, c }) {
  const ref = useRef(null);
  const isPredictive = alert.category === 'predictive';
  const [rangeKey, setRangeKey] = useState('7d');

  const anchorIso = alert.firstDetectedAt ?? alert.generatedAt ?? null;
  const rangeDays = TREND_RANGES.find((r) => r.key === rangeKey)?.days ?? 7;
  const { from, to } = useMemo(() => lastNDaysRangeFrom(rangeDays, anchorIso), [rangeDays, anchorIso]);
  const { data: trendData, isLoading } = useParameterCharts(
    alert.deploymentId, alert.parameterId, from, to, 'run_chart',
  );

  const series         = (alert.series?.length ? alert.series : null) ?? (trendData?.runChart?.points ?? []);
  const forecastSeries = isPredictive ? (alert.forecastSeries ?? []) : [];
  const timestamps     = (alert.timestamps?.length ? alert.timestamps : null) ?? (trendData?.runChart?.timestamps ?? []);
  const optimalMin     = trendData?.runChart?.optimalMin ?? alert.optimalMin ?? null;
  const optimalMax     = trendData?.runChart?.optimalMax ?? alert.optimalMax ?? null;
  const criticalMin    = alert.criticalMin ?? null;
  const criticalMax    = alert.criticalMax ?? null;
  const unit           = alert.unit ?? '';

  const histLabels = (isLoading || !series.length) ? [] : (timestamps.length ? timestamps.map(fmtLabel) : series.map((_, i) => `T${i + 1}`));
  const fcLabels   = (forecastSeries ?? []).map((_, i) => `F${i + 1}`);
  const labels     = [...histLabels, ...fcLabels];

  const ptColors = series.map((v) => {
    const ok = (optimalMin == null || v >= optimalMin) && (optimalMax == null || v <= optimalMax);
    return ok ? 'rgba(22,163,74,0.85)' : 'rgba(220,38,38,0.85)';
  });

  const datasets = [
    {
      label: 'Value',
      data: [...series, ...Array((forecastSeries ?? []).length).fill(null)],
      borderColor: 'rgba(30,67,150,0.7)', borderWidth: 1.8,
      pointRadius: 2, pointBackgroundColor: ptColors, pointBorderColor: ptColors,
      tension: 0.3, fill: false,
    },
  ];

  if ((forecastSeries ?? []).length && series.length > 0) {
    datasets.push({
      label: 'Forecast',
      data: [...Array(series.length - 1).fill(null), series[series.length - 1], ...(forecastSeries ?? [])],
      borderColor: semantic.violet, borderWidth: 1.8, borderDash: [5, 4],
      pointRadius: 0, fill: false, tension: 0.3,
    });
  }

  if (optimalMin != null) datasets.push({ label: `Optimal min (${optimalMin})`, data: Array(labels.length).fill(optimalMin), borderColor: 'rgba(22,163,74,0.7)', borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, fill: false, tension: 0 });
  if (optimalMax != null) datasets.push({ label: `Optimal max (${optimalMax})`, data: Array(labels.length).fill(optimalMax), borderColor: 'rgba(22,163,74,0.7)', borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, fill: optimalMin != null ? '-1' : false, backgroundColor: 'rgba(22,163,74,0.14)', tension: 0 });
  if (criticalMin != null) datasets.push({ label: `Critical min (${criticalMin})`, data: Array(labels.length).fill(criticalMin), borderColor: 'rgba(220,38,38,0.55)', borderWidth: 1.2, borderDash: [2, 3], pointRadius: 0, fill: false, tension: 0 });
  if (criticalMax != null) datasets.push({ label: `Critical max (${criticalMax})`, data: Array(labels.length).fill(criticalMax), borderColor: 'rgba(220,38,38,0.55)', borderWidth: 1.2, borderDash: [2, 3], pointRadius: 0, fill: false, tension: 0 });

  const tickColor = c.textSubtle;
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    layout: { padding: { left: 4 } },
    plugins: {
      legend: { display: true, position: 'bottom', labels: { color: tickColor, font: { size: 9 }, boxWidth: 10, padding: 8, usePointStyle: true } },
      tooltip: {
        mode: 'nearest', intersect: false,
        filter: (item) => item.datasetIndex === 0 || item.datasetIndex === 1,
        callbacks: {
          title: (items) => {
            if (!timestamps?.length || !items.length) return '';
            const ts = timestamps[items[0].dataIndex];
            if (!ts) return '';
            return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          },
          label: (ctx) => ` ${fmt2(ctx.parsed.y)} ${unit ?? ''}`,
        },
      },
    },
    scales: {
      // offset+autoSkip:false centers and labels the lone point when there is only one datum (AUT-131)
      x: { offset: labels.length <= 1, ticks: { color: tickColor, font: { size: 9 }, maxRotation: 45, autoSkip: labels.length > 1, maxTicksLimit: 10 }, grid: { color: c.border } },
      y: { ticks: { color: tickColor, font: { size: 9 } }, grid: { color: c.border } },
    },
  };
  opts.plugins.zoom = ZOOM_CFG;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Trend</span>
        <RangePicker ranges={TREND_RANGES} value={rangeKey} onChange={setRangeKey} c={c} size="sm" />
      </div>
      <div style={{ height: 200, position: 'relative' }}>
        {isLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textSubtle, fontSize: typography.fontSize.base }}>Loading…</div>
        ) : !series.length ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textSubtle, fontSize: typography.fontSize.base }}>No trend data</div>
        ) : (
          <>
            <Line ref={ref} data={{ labels, datasets }} options={opts} />
            <button
              onClick={() => ref.current?.resetZoom?.()}
              title="Reset zoom"
              style={{
                position: 'absolute', top: 4, right: 4,
                padding: '2px 7px', borderRadius: radius.sm, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
                border: `1px solid ${c.border}`, background: c.bg, color: c.textSubtle,
                cursor: 'pointer', fontFamily: typography.fontFamily.body, opacity: 0.8,
              }}
            >Reset</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Alert Detail Modal ────────────────────────────────────────────────────────
function AlertModal({ alert, isOpen, c, isDark, onClose, onExited, alerts = [], alertIndex = 0, onNavigate }) {
  const { isMobile } = useBreakpoint();

  // keyboard nav
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft') onNavigate?.((alertIndex - 1 + alerts.length) % alerts.length);
      if (e.key === 'ArrowRight') onNavigate?.((alertIndex + 1) % alerts.length);
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alertIndex, alerts.length, onNavigate, onClose]);

  if (!alert) return null;
  const catMeta = ALERT_CATEGORY_META[alert.category] ?? {};
  const sevColor = severityColor(alert.severity);

  return (
    <SlideModal isOpen={isOpen} onClose={onClose} onExited={onExited} maxWidth={560} borderRadius={14} c={c} isDark={isDark}>
      <div style={{ padding: isMobile ? 16 : 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 22, color: catMeta.color, flexShrink: 0, marginTop: 2 }}
          >
            {catMeta.icon}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={alert.parameterName}>
              {alert.parameterName}
            </div>
            <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, marginTop: 2 }}>{alert.layerName ?? alert.dataKey}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 7px', borderRadius: radius.sm, background: catBg(catMeta, isDark), color: catMeta.color, border: `1px solid ${catMeta.border}` }}>
              {catMeta.label}
            </span>
            <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 7px', borderRadius: radius.sm, background: sevBg(SEVERITY_META[alert.severity] ?? {}, isDark), color: sevColor, border: `1px solid ${SEVERITY_META[alert.severity]?.border}` }}>
              {SEVERITY_META[alert.severity]?.label ?? alert.severity}
            </span>
          </div>
          {/* Nav prev/next + close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {alerts.length > 1 && (
              <>
                <button onClick={() => onNavigate?.((alertIndex - 1 + alerts.length) % alerts.length)} title="Previous (←)" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: c.textSubtle, borderRadius: radius.sm }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
                </button>
                <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, minWidth: 28, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {alertIndex + 1}/{alerts.length}
                </span>
                <button onClick={() => onNavigate?.((alertIndex + 1) % alerts.length)} title="Next (→)" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: c.textSubtle, borderRadius: radius.sm }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
                </button>
                <div style={{ width: 1, height: 14, background: c.border, margin: '0 2px' }} />
              </>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSubtle, padding: 4, borderRadius: radius.sm }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
            </button>
          </div>
        </div>

        {/* Current reading + Off Since (AUT-171) */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            {/* Stale: show last reading value + time instead of current/off-duration */}
            {alert.category === 'stale' ? (
              <>
                <div>
                  <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
                    Last reading
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sevColor, fontVariantNumeric: 'tabular-nums' }}>
                      {alert.value != null ? fmt2(alert.value) : '—'}
                    </span>
                    <span style={{ fontSize: typography.fontSize.lg, color: c.textSubtle }}>{alert.unit ?? ''}</span>
                  </div>
                </div>
                {alert.lastReading && (
                  <div>
                    <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
                      Last reading time
                    </div>
                    <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtTs(alert.lastReading)}
                    </div>
                    <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, marginTop: 2 }}>{relTime(alert.lastReading)}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Current reading — live alerts only; history shows off-band avg instead */}
                {!alert.resolvedAt && (
                  <div>
                    <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
                      Current reading
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sevColor, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt2(alert.value)}
                      </span>
                      <span style={{ fontSize: typography.fontSize.lg, color: c.textSubtle }}>{alert.unit ?? ''}</span>
                    </div>
                  </div>
                )}
                {/* Off-band average (history) — replaces Current reading in the same slot/color */}
                {alert.offBandAvg != null && (
                  <div>
                    <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
                      Off-band avg
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sevColor, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt2(alert.offBandAvg)}
                      </span>
                      <span style={{ fontSize: typography.fontSize.lg, color: c.textSubtle }}>{alert.unit ?? ''}</span>
                    </div>
                  </div>
                )}
                {/* Off Since — minutes/hours/days via fmtDuration */}
                {(() => {
                  const offMins = alert.offBandMins;
                  if (offMins == null) return null;
                  return (
                    <div>
                      <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
                        {alert.resolvedAt ? 'Off duration' : 'Off since'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sevColor, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtDuration(offMins)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <BandBar alert={alert} c={c} isDark={isDark} />
          {/* History: actual start/end timestamps (AUT-185) */}
          {alert.firstDetectedAt && (
            <div style={{ marginTop: 6, display: 'flex', gap: 16, fontSize: typography.fontSize.sm, color: c.textSubtle }}>
              <span>Started <strong style={{ color: c.textPrimary, fontWeight: typography.fontWeight.semibold }}>{fmtTs(alert.firstDetectedAt)}</strong></span>
              {alert.resolvedAt && (
                <span>Ended <strong style={{ color: c.textPrimary, fontWeight: typography.fontWeight.semibold }}>{fmtTs(alert.resolvedAt)}</strong></span>
              )}
            </div>
          )}
        </div>

        {/* Predictive: expected breach chip — above trend chart */}
        {(alert.forecastSeries ?? []).length > 0 && (
          <div style={{ fontSize: typography.fontSize.sm, color: semantic.violet, marginBottom: 10, padding: '8px 12px', borderRadius: radius.md, background: isDark ? 'rgba(124,58,237,0.12)' : 'rgba(124,58,237,0.07)', border: `1px solid ${semantic.violetBorder}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 15, flexShrink: 0 }}>schedule</span>
            <span>
              Forecast to breach optimal range
              {alert.expectedBreachAt && fmtRelativeFuture(alert.expectedBreachAt) && (
                <> — <strong style={{ fontWeight: typography.fontWeight.bold }}>{fmtRelativeFuture(alert.expectedBreachAt)}</strong> ({fmtTs(alert.expectedBreachAt)})</>
              )}
            </span>
          </div>
        )}

        {/* Stale info chip — above trend chart; only for live (unresolved) stale alerts */}
        {alert.category === 'stale' && !alert.resolvedAt && (
          <div style={{ padding: '10px 12px', borderRadius: radius.md, background: isDark ? 'rgba(8,145,178,0.18)' : 'rgba(8,145,178,0.08)', border: `1px solid ${semantic.infoCyanBorderStrong}`, marginBottom: 10, fontSize: typography.fontSize.base, color: semantic.infoCyan }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }}>sensors_off</span>
            No new data received{alert.staleSinceMin != null ? <> for <strong>{fmtDuration(alert.staleSinceMin)}</strong></> : null}{alert.staleAfterMin != null ? <>. Expected refresh every <strong>{fmtDuration(alert.staleAfterMin)}</strong></> : null}.
          </div>
        )}

        {/* Trend chart */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 8 }}>
            Parameter Trend{(alert.forecastSeries ?? []).length ? ' + Forecast' : ''}
          </div>
          <AlertTrendChart alert={alert} c={c} />
        </div>

        {/* Reco CTA — always rendered to keep height stable */}
        {alert.recId ? (
          <a
            href={`/deployments/${alert.deploymentId}/recommendations?rec=${alert.recId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: radius.lg, textDecoration: 'none',
              background: isDark ? 'rgba(30,67,150,0.22)' : 'rgba(30,67,150,0.07)',
              border: `1px solid ${semantic.brandBorder}`,
              color: isDark ? '#93c5fd' : semantic.brandNavyMid, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lightbulb</span>
              View linked recommendation
            </span>
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>open_in_new</span>
          </a>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 14px', borderRadius: radius.lg,
            background: isDark ? 'rgba(255,255,255,0.03)' : c.bgSubtle,
            border: `1px solid ${c.border}`,
            fontSize: typography.fontSize.base, color: c.textSubtle,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, opacity: 0.4 }}>lightbulb</span>
            No linked recommendation
          </div>
        )}
      </div>
    </SlideModal>
  );
}

// ── Band gauge: optimal zone + critical ticks + current-value needle ──────────
const RED = semantic.error;
function BandBar({ alert, c, isDark }) {
  const { optimalMin: lo, optimalMax: hi, criticalMin: clo, criticalMax: chi, value: v } = alert;
  const sevColor = severityColor(alert.severity);
  const pts = [lo, hi, clo, chi, v].filter((n) => n != null);
  const dataMin = Math.min(...pts), dataMax = Math.max(...pts);
  const spanRaw = (dataMax - dataMin) || Math.abs(v) || 1;
  // 10% padding each side — enough breathing room for edge labels (needle/optimal near boundary)
  const min = dataMin - spanRaw * 0.10, max = dataMax + spanRaw * 0.10;
  const pctNum = (n) => Math.max(0, Math.min(100, ((n - min) / (max - min)) * 100));
  const pct = (n) => `${pctNum(n)}%`;

  return (
    <div>
      {/* Track */}
      <div style={{ position: 'relative', height: 16, borderRadius: radius.md, background: c.bgMuted ?? c.bgSubtle }}>
        {/* optimal band — clearly visible green zone; supports directional (one-sided) ranges */}
        {(lo != null || hi != null) && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: lo != null ? pct(lo) : '0%',
            right: hi != null ? `calc(100% - ${pct(hi)})` : '0%',
            background: isDark ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.28)',
            borderRadius: radius.sm,
          }} />
        )}
        {/* critical low tick */}
        {clo != null && (
          <div title={`Critical low: ${clo}`} style={{
            position: 'absolute', top: -3, bottom: -3, left: pct(clo),
            width: 2, background: RED, transform: 'translateX(-50%)',
          }} />
        )}
        {/* critical high tick */}
        {chi != null && (
          <div title={`Critical high: ${chi}`} style={{
            position: 'absolute', top: -3, bottom: -3, left: pct(chi),
            width: 2, background: RED, transform: 'translateX(-50%)',
          }} />
        )}
        {/* current value needle */}
        {v != null && (
          <div style={{
            position: 'absolute', top: -5, bottom: -5, left: pct(v),
            width: 3, background: sevColor, borderRadius: radius.xs, transform: 'translateX(-50%)',
          }} />
        )}
      </div>

      {/* Per-tick labeled axis — each label anchored to its exact position */}
      <div style={{ position: 'relative', height: 30, marginTop: 4, fontSize: typography.fontSize.xs, overflow: 'visible' }}>
        {/* Critical low label */}
        {clo != null && (
          <span style={{
            position: 'absolute', left: pct(clo), transform: 'translateX(-50%)',
            textAlign: 'center', whiteSpace: 'nowrap',
          }}>
            <span style={{ display: 'block', fontSize: typography.fontSize.xs, color: c.textSubtle, lineHeight: 1.2 }}>Crit. Low</span>
            <span style={{ color: RED, fontWeight: typography.fontWeight.semibold }}>{clo}</span>
          </span>
        )}
        {/* Optimal range label — centered inside the green band; supports directional ranges */}
        {(lo != null || hi != null) && (
          <span style={{
            position: 'absolute',
            left: lo != null && hi != null
              ? `${(pctNum(lo) + pctNum(hi)) / 2}%`
              : lo != null ? `${(pctNum(lo) + 100) / 2}%` : `${pctNum(hi) / 2}%`,
            transform: 'translateX(-50%)',
            textAlign: 'center', whiteSpace: 'nowrap',
          }}>
            <span style={{ display: 'block', fontSize: typography.fontSize.xs, color: c.textSubtle, lineHeight: 1.2 }}>Optimal</span>
            <span style={{ color: semantic.successGreen, fontWeight: typography.fontWeight.semibold }}>
              {lo != null && hi != null ? `${lo}–${hi}` : lo != null ? `≥${lo}` : `≤${hi}`}
              {alert.unit ? ` ${alert.unit}` : ''}
            </span>
          </span>
        )}
        {/* Critical high label */}
        {chi != null && (
          <span style={{
            position: 'absolute', left: pct(chi), transform: 'translateX(-50%)',
            textAlign: 'center', whiteSpace: 'nowrap',
          }}>
            <span style={{ display: 'block', fontSize: typography.fontSize.xs, color: c.textSubtle, lineHeight: 1.2 }}>Crit. High</span>
            <span style={{ color: RED, fontWeight: typography.fontWeight.semibold }}>{chi}</span>
          </span>
        )}
        {/* "Now" value intentionally omitted — displayed prominently in Current Reading above */}
      </div>
    </div>
  );
}

// ── Alert Chip (card in the kanban grid) ──────────────────────────────────────
function AlertChip({ alert, onClick, c, isDark }) {
  const [hovered, setHovered] = useState(false);
  const catMeta = ALERT_CATEGORY_META[alert.category] ?? {};
  const sevColor = severityColor(alert.severity);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Alert: ${alert.parameterName}`}
      onClick={() => onClick(alert)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(alert); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 12px', borderRadius: radius.lg, cursor: 'pointer',
        borderTop: `1px solid ${hovered ? catMeta.color : c.border}`,
        borderRight: `1px solid ${hovered ? catMeta.color : c.border}`,
        borderBottom: `1px solid ${hovered ? catMeta.color : c.border}`,
        borderLeft: `3px solid ${sevColor}`,
        background: hovered ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)') : c.bg,
        transition: 'border-color 0.15s, background 0.15s',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      {/* Param name + severity dot */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, lineHeight: 1.3, wordBreak: 'break-word' }}>
            {alert.parameterName}
          </div>
          <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 1 }}>{alert.layerName ?? alert.dataKey}</div>
        </div>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: sevColor, flexShrink: 0, marginTop: 4 }} />
      </div>

      {/* Value row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: alert.isStale ? semantic.infoCyan : sevColor }}>
          {alert.isStale ? 'STALE' : `${fmt2(alert.value)} ${alert.unit ?? ''}`}
        </span>
        {alert.isStale && (
          <span style={{ fontSize: typography.fontSize.xs, color: semantic.infoCyan }}>{fmtDuration(alert.staleSinceMin)} stale</span>
        )}
        {!alert.isStale && alert.offBandMins != null && (
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{fmtDuration(alert.offBandMins)} off</span>
        )}
      </div>

      {/* Predictive: expected breach time */}
      {alert.category === 'predictive' && alert.expectedBreachAt && fmtRelativeFuture(alert.expectedBreachAt) && (
        <div style={{ fontSize: typography.fontSize.xs, color: semantic.violet, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>schedule</span>
          Breach {fmtRelativeFuture(alert.expectedBreachAt)}
        </div>
      )}

      {/* Footer: param type + last reading + reco indicator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label }}>
          {alert.paramType}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {alert.recId && (
            <span title="Has linked recommendation" className="material-symbols-outlined" style={{ fontSize: 12, color: c.textActive }}>lightbulb</span>
          )}
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{relTime(alert.lastReading)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Category Column ───────────────────────────────────────────────────────────
function CategoryColumn({ catKey, alerts, onChipClick, c, isDark }) {
  const meta = ALERT_CATEGORY_META[catKey];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden' }}>
      {/* Column header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10,
        padding: '6px 10px', borderRadius: radius.md, flexShrink: 0,
        background: catBg(meta, isDark), border: `1px solid ${meta.border}`,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 14, color: meta.color }}>
          {meta.icon}
        </span>
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: meta.color, flex: 1 }}>{meta.label}</span>
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, minWidth: 18, height: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: radius.lg, background: meta.color, color: '#fff',
        }}>
          {alerts.length}
        </span>
      </div>

      {/* Chips — only this scrolls. Equal padding all sides = scrollbar width, so the
          bar sits in the right gutter and never overlaps a chip. The bg + radius
          encapsulate each list as its own panel (AUT-165). */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, padding: KANBAN_SCROLL_PAD, background: c.bgSubtle, borderRadius: radius.lg }}>
        {alerts.length === 0 ? (
          <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: typography.fontSize.sm, color: c.textSubtle, borderRadius: radius.md, border: `1px dashed ${c.border}` }}>
            No alerts
          </div>
        ) : (
          alerts.map((a) => (
            <AlertChip key={a.id} alert={a} onClick={onChipClick} c={c} isDark={isDark} />
          ))
        )}
      </div>
    </div>
  );
}

// ── History Table ─────────────────────────────────────────────────────────────
function useHistoryColumns(c, onOpen) {
  return useMemo(() => [
    {
      field: 'parameterName',
      headerName: 'Parameter',
      flex: 1.6,
      minWidth: 180,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 0' }}>
          <span style={{ fontWeight: typography.fontWeight.bold, fontSize: typography.fontSize.md, color: c.textPrimary }}>{row.parameterName}</span>
          <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle }}>{row.layerName ?? row.variableType}</span>
        </div>
      ),
    },
    {
      field: 'category',
      headerName: 'Alert type',
      flex: 1,
      minWidth: 130,
      valueGetter: (_v, row) => row.category,
      renderCell: ({ row }) => {
        const cm = ALERT_CATEGORY_META[row.category] ?? {};
        return (
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, padding: '2px 8px', borderRadius: radius.sm, background: catBg(cm, false), color: cm.color, border: `1px solid ${cm.border}` }}>
            {cm.label ?? row.category}
          </span>
        );
      },
    },
    {
      field: 'severity',
      headerName: 'Peak severity',
      flex: 1,
      minWidth: 120,
      valueGetter: (_v, row) => row.severity,
      renderCell: ({ row }) => {
        const sm = SEVERITY_META[row.severity] ?? {};
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: sm.color }} />
            <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: sm.color }}>{sm.label ?? row.severity}</span>
          </span>
        );
      },
    },
    {
      field: 'firstDetectedAt',
      headerName: 'First detected',
      flex: 1.2,
      minWidth: 140,
      renderCell: ({ value }) => {
        if (!value) return <span style={{ color: c.textSubtle }}>—</span>;
        const exact = (() => { try { return new Date(value).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return value; } })();
        return (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{relTime(value)}</div>
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 2 }}>{exact}</div>
          </div>
        );
      },
    },
    {
      field: 'durationLabel',
      headerName: 'Time to resolve',
      flex: 0.9,
      minWidth: 110,
      renderCell: ({ value }) => (
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{value ?? '—'}</span>
      ),
    },
    {
      field: '__actions',
      headerName: '',
      width: 96,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); onOpen?.(row); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 3,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textActive,
            fontFamily: typography.fontFamily.body,
          }}
        >
          Details<span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        </button>
      ),
    },
  ], [c, onOpen]);
}

function HistoryTable({ alerts, onRowClick, isLoading, c, serverPagination }) {
  const columns = useHistoryColumns(c, onRowClick);
  if (!isLoading && !alerts.length) return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '12px' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textSubtle, opacity: 0.35 }}>history</span>
      <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No historical alerts in this range.</p>
    </div>
  );
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AppTable
        rows={alerts}
        columns={columns}
        getRowId={(r) => r.id}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 64}
        onRowClick={({ row }) => onRowClick?.(row)}
        loading={isLoading}
        serverPagination={serverPagination}
        disableColumnMenu
        disableColumnSorting
        extraSx={{
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: '8px' },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DeploymentAlertsPage() {
  const { id: depId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile, isTablet } = useBreakpoint();

  const view = searchParams.get('view') === 'historical' ? 'historical' : 'live';
  const alertIdParam = searchParams.get('alert');

  const [selectedAlert, setSelectedAlert] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [histDateRange, setHistDateRange] = useState('7d');
  const closeGenRef = useRef(0);

  function setView(v) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('view', v);
      p.delete('alert');
      return p;
    }, { replace: true });
    setFiltersOpen(false);
  }

  function openAlert(a) {
    setSelectedAlert(a);
    setModalOpen(true);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('alert', a.id);
      return p;
    }, { replace: true });
  }

  function closeAlert() {
    setModalOpen(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('alert');
      return p;
    }, { replace: true });
    const gen = ++closeGenRef.current;
    setTimeout(() => { if (closeGenRef.current === gen) setSelectedAlert(null); }, 300);
  }

  // DataGrid paginationModel is 0-indexed; the BE is 1-indexed. The query key is a
  // DEBOUNCED copy of the model so rapid page clicks fire a single request for the
  // final page. The grid is bound to the SAME debounced model, so the rendered
  // rows and the active page never desync (server mode renders by page window).
  const [histModel, setHistModel] = useState({ page: 0, pageSize: 10 });
  const debouncedModel = useDebouncedValue(histModel, 350);

  const { data: filterSchema } = useAlertsFilterSchema(depId);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [committedFilters, setCommittedFilters] = useState({});
  const [committedSort, setCommittedSort] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterParams = useMemo(
    () => buildFilterParams(committedFilters, debouncedSearch, committedSort),
    [committedFilters, debouncedSearch, committedSort],
  );

  const histParams = useMemo(
    () => ({ ...histDateParams(histDateRange), ...filterParams, page: debouncedModel.page + 1, pageSize: debouncedModel.pageSize }),
    [histDateRange, filterParams, debouncedModel.page, debouncedModel.pageSize],
  );
  const { data: liveAlerts = [], isLoading: loadingLive, isError: errorLive, error: errLive }   = useAlertsLive(depId);
  const { data: histData, isLoading: loadingHist, isFetching: fetchingHist, isError: errorHist, error: errHist } = useAlertsHistorical(depId, histParams);
  const histAlerts = useMemo(() => histData?.rows ?? [], [histData]);
  const histPagination = histData?.pagination ?? null;
  // Busy while the debounce is pending (model ahead of query) or a fetch runs.
  const histBusy = fetchingHist || search !== debouncedSearch || histModel.page !== debouncedModel.page || histModel.pageSize !== debouncedModel.pageSize;

  // Reset to the first page whenever the date range changes (the current page
  // may be out of range for the new window).
  function changeHistRange(range) {
    setHistDateRange(range);
    setHistModel((m) => ({ ...m, page: 0 }));
  }

  const activeFilterCount = useMemo(() => {
    let n = Object.values(committedFilters).filter((e) => {
      if (!e) return false;
      if (e.type === 'categorical') return (e.value ?? []).length > 0;
      return (e.min ?? '') !== '' || (e.max ?? '') !== '';
    }).length;
    if (committedSort?.by) n += 1;
    return n;
  }, [committedFilters, committedSort]);

  function applyFilters(filters, sort) {
    setCommittedFilters(filters);
    setCommittedSort(sort);
    setFiltersOpen(false);
    setHistModel((m) => ({ ...m, page: 0 }));
  }
  function clearFilters() {
    setCommittedFilters({});
    setCommittedSort(null);
    setHistModel((m) => ({ ...m, page: 0 }));
  }
  function changeSearch(text) {
    setSearch(text);
    setHistModel((m) => ({ ...m, page: 0 }));
  }

  // Restore alert modal from URL param once data loads (don't write URL — it's already there)
  useEffect(() => {
    if (!alertIdParam || modalOpen) return;
    const all = [...liveAlerts, ...histAlerts];
    const match = all.find((a) => a.id === alertIdParam);
    if (match) { setSelectedAlert(match); setModalOpen(true); }
  }, [alertIdParam, liveAlerts, histAlerts, modalOpen]);

  const isLoading = view === 'live' ? loadingLive : loadingHist;
  const isError = view === 'live' ? errorLive : errorHist;
  const loadError = view === 'live' ? errLive : errHist;
  const rawAlerts = view === 'live' ? liveAlerts : histAlerts;


  const filtered = rawAlerts;

  const byCategory = useMemo(() => {
    const map = {};
    ALERT_CATEGORY_ORDER.forEach((k) => { map[k] = []; });
    filtered.forEach((a) => {
      if (map[a.category]) map[a.category].push(a);
    });
    return map;
  }, [filtered]);

  const totalCount = view === 'historical' ? (histPagination?.total ?? histAlerts.length) : rawAlerts.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PageHeader
        title="Alerts"
        description={`${totalCount} ${view === 'historical' ? 'historical' : 'active'} alert${totalCount !== 1 ? 's' : ''}`}
        actions={<ViewToggle view={view} onChange={setView} c={c} isDark={isDark} />}
      />

      {/* Filters toolbar */}
      {view === 'historical' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <HistoricalFilterBar
            dateRange={histDateRange}
            onDateRange={changeHistRange}
            c={c}
            isDark={isDark}
            search={search}
            onSearch={changeSearch}
            onOpenFilters={filterSchema ? () => setFiltersOpen(true) : undefined}
            activeFilterCount={activeFilterCount}
          />
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isLoading && view === 'live' ? (
          <div style={{ height: '100%', overflowY: 'auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14, alignContent: 'start' }}>
            {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} rows={3} height={100} />)}
          </div>
        ) : isError ? (
          <ErrorState message={loadError?.message ?? 'Failed to load alerts.'} />
        ) : view === 'live' ? (
          <div style={{ height: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14, alignItems: 'stretch' }}>
            {ALERT_CATEGORY_ORDER.map((catKey) => (
              <CategoryColumn
                key={catKey}
                catKey={catKey}
                alerts={byCategory[catKey] ?? []}
                onChipClick={(a) => openAlert(a)}
                c={c}
                isDark={isDark}
              />
            ))}
          </div>
        ) : (
          <HistoryTable
            alerts={histAlerts}
            onRowClick={(a) => openAlert(a)}
            isLoading={loadingHist || histBusy}
            c={c}
            serverPagination={histPagination ? {
              rowCount: histPagination.total,
              paginationModel: histModel,
              onPaginationModelChange: setHistModel,
            } : undefined}
          />
        )}
      </div>

      {/* Filter drawer */}
      {view === 'historical' && filterSchema && (
        <FilterDrawer
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          schema={filterSchema}
          initialFilters={committedFilters}
          initialSort={committedSort}
          onApply={applyFilters}
          onClear={clearFilters}
        />
      )}

      {/* Alert detail modal */}
      {selectedAlert && (
        <AlertModal
          alert={selectedAlert}
          isOpen={modalOpen}
          c={c}
          isDark={isDark}
          onClose={closeAlert}
          onExited={() => setSelectedAlert(null)}
          alerts={view === 'live' ? (byCategory[selectedAlert?.category] ?? []) : filtered}
          alertIndex={view === 'live'
            ? (byCategory[selectedAlert?.category] ?? []).findIndex((a) => a.id === selectedAlert.id)
            : filtered.findIndex((a) => a.id === selectedAlert.id)}
          onNavigate={(idx) => {
            const pool = view === 'live' ? (byCategory[selectedAlert?.category] ?? []) : filtered;
            openAlert(pool[idx]);
          }}
        />
      )}
    </div>
  );
}
